import type {
  Actuator,
  ActuatorUUID,
  Body,
  BodyUUID,
  ContactExclude,
  Equality,
  EqualityUUID,
  Joint,
  JointUUID,
  Site,
  SiteUUID,
  Tendon,
  TendonUUID,
} from "../../types/scene";
import * as THREE from "three";
import * as RuntimeRegistry from "../../runtime/objectRegistry";
import { create } from "mutative";
import { logger } from "../../utils/logger";
import { shouldBlockPhysicsMutation } from "../../utils/physicsGuards";
import { cleanupReferencesForDeletedTargets } from "./referenceCleanup";
import { embedJointsIntoBodies } from "./jointActions";
import { embedSitesIntoBodies } from "./siteActions";
import { getBodyTree, type BodyWithChildren } from "./bodyUtils";
import { withHistory } from "./withHistory";
import type { SceneStoreSetState } from "../sceneStore.types";
import type { BodyActions } from "./actionTypes";
import {
  collectGeomMeshUuids,
  flattenBodyTree,
  pruneOrphanMeshes,
  type BodyRecord,
  type BodyTreeNode,
  type JointRef,
  type RuntimeRegistryMap,
  type TendonJointMember,
  type WorldBodyRef,
} from "./bodyActionUtils";

export const createBodyRemovalActions = (
  set: SceneStoreSetState
): Pick<BodyActions, "removeBody" | "removeBodyObject"> => ({
  // Example of a cross-domain operation: removes body AND its joints
  // Accepts body UUID only
  removeBody: withHistory((bodyUuid: BodyUUID) => {
    if (shouldBlockPhysicsMutation("removeBody")) return;
    let shouldDispatchStructureChanged = false;
    set((state) =>
      create(state, (draft) => {
        const liftedChildren: Body[] = [];

        const getObject = (uuid: string): THREE.Object3D | null => {
          try {
            const reg = (RuntimeRegistry.getAll() || {}) as RuntimeRegistryMap;
            return reg[uuid] || null;
          } catch {
            return null;
          }
        };

        const findModelGroup = (): THREE.Object3D | null => {
          const reg = (RuntimeRegistry.getAll() || {}) as RuntimeRegistryMap;
          const keys = Object.keys(reg);
          if (keys.length === 0) return null;
          let obj: THREE.Object3D | undefined = reg[keys[0]];
          while (obj && obj.parent) {
            if (obj.name === "modelGroup") return obj;
            obj = obj.parent;
          }
          return null;
        };

        const modelGroup = findModelGroup();
        const modelGroupWorld = modelGroup ? modelGroup.matrixWorld.clone() : new THREE.Matrix4();
        const modelGroupWorldInv = modelGroupWorld.clone();
        if (modelGroup) modelGroupWorldInv.invert();

        // Find the body and collect its joints from flat map (no tree traversal needed)
        const findBodyAndCollectJoints = (
          bodiesMap: BodyRecord,
          targetUuid: string
        ): { body: Body | null; jointRefs: JointRef[] } => {
          const body = bodiesMap[targetUuid] || null;
          if (!body) {
            logger.error(`[removeBody] Body UUID not found: ${targetUuid}`);
            return { body: null, jointRefs: [] };
          }
          const jointRefs = Array.isArray(body.joints) ? (body.joints as JointRef[]) : [];
          return { body, jointRefs };
        };

        const { body: bodyToDelete, jointRefs } = findBodyAndCollectJoints(state.bodies, bodyUuid);

        const deletedJointTargets = new Set<string>();
        // Delete joints belonging to this body before removing the body
        if (bodyToDelete && jointRefs.length > 0) {
          const jointMap = draft.joints;
          const jointNamesToDelete = new Set<string>();

          // Resolve joint references to get their names/uuids
          jointRefs.forEach((ref) => {
            if (typeof ref === "string") {
              // String reference can be a map key, uuid, or name.
              if (jointMap[ref as JointUUID]) {
                jointNamesToDelete.add(ref);
              }
              Object.entries(jointMap as Record<string, Joint>).forEach(([key, joint]) => {
                if (joint && (joint.uuid === ref || joint.name === ref)) {
                  jointNamesToDelete.add(key);
                }
              });
            } else if (ref && typeof ref === "object") {
              // Object reference - use uuid or name
              const identifier = ref.uuid ?? ref.name;
              if (identifier) {
                Object.entries(jointMap as Record<string, Joint>).forEach(([key, joint]) => {
                  if (joint && (joint.uuid === identifier || joint.name === identifier)) {
                    jointNamesToDelete.add(key);
                  }
                });
              }
            }
          });

          // Delete the joints
          jointNamesToDelete.forEach((key) => {
            const jointKey = key as JointUUID;
            const joint = draft.joints?.[jointKey];
            if (joint) {
              deletedJointTargets.add(key);
              if (joint.uuid) deletedJointTargets.add(joint.uuid);
              if (joint.name) deletedJointTargets.add(joint.name);
            }
            delete draft.joints[jointKey];
          });
        }

        const siteUuidsToDelete = new Set<SiteUUID>();
        const collectSitesForRemoval = (node: BodyWithChildren | null | undefined) => {
          if (!node) return;
          const entries = (Array.isArray(node.sites) ? node.sites : []) as Array<SiteUUID | Site>;
          entries.forEach((entry) => {
            if (!entry) return;
            if (typeof entry === "string") {
              siteUuidsToDelete.add(entry as SiteUUID);
            } else {
              siteUuidsToDelete.add(entry.uuid as SiteUUID);
            }
          });
        };

        const removeBodyFromTree = (nodes: BodyWithChildren[]): BodyWithChildren[] => {
          const result: BodyWithChildren[] = [];
          for (const node of nodes) {
            // Match by UUID only
            if (node.uuid === bodyUuid) {
              collectSitesForRemoval(node);
              const children = node.children || [];
              children.forEach((child) => {
                const childUuid = child?.uuid;
                if (!childUuid) {
                  logger.error("[removeBody] Child missing UUID, cannot preserve world transform.");
                  return;
                }
                const childObj = getObject(childUuid);
                if (childObj) childObj.updateMatrixWorld(true);
                const childWorld = childObj ? childObj.matrixWorld.clone() : new THREE.Matrix4();
                const localMat = modelGroup
                  ? modelGroupWorldInv.clone().multiply(childWorld)
                  : childWorld.clone();
                const p = new THREE.Vector3();
                const q = new THREE.Quaternion();
                const s = new THREE.Vector3();
                localMat.decompose(p, q, s);
                liftedChildren.push({
                  ...child,
                  position: [p.x, p.y, p.z],
                  orientation: [q.w, q.x, q.y, q.z],
                } as unknown as Body);
              });
              continue;
            }
            const newChildren =
              node.children && node.children.length > 0
                ? removeBodyFromTree(node.children)
                : node.children;
            result.push({ ...node, children: newChildren || [] });
          }
          return result;
        };

        const bodyTree = getBodyTree(state);
        const pruned = removeBodyFromTree(bodyTree);

        // Filter out lifted children that are already in the pruned tree to avoid duplicates
        const existingUuids = new Set<string>();
        const collectUuids = (nodes: BodyWithChildren[]) => {
          nodes.forEach((node) => {
            if (node?.uuid) existingUuids.add(node.uuid);
            if (node.children && node.children.length > 0) {
              collectUuids(node.children);
            }
          });
        };
        collectUuids(pruned);

        // Only add lifted children that aren't already in the tree
        const uniqueLiftedChildren = liftedChildren.filter(
          (child) => child?.uuid && !existingUuids.has(child.uuid)
        );

        const updatedBodies = [...pruned, ...uniqueLiftedChildren];

        embedJointsIntoBodies(updatedBodies, draft.joints);

        siteUuidsToDelete.forEach((uuid) => {
          delete draft.sites[uuid];
        });

        // Cascade-delete tendons that reference deleted joints.
        const tendonUuidsToDelete = new Set<string>();
        const deletedTendonTargets = new Set<string>();
        Object.entries((draft.tendons || {}) as Record<string, Tendon>).forEach(
          ([tendonUuid, tendon]) => {
            if (!tendon) return;
            const members = Array.isArray(tendon.joints) ? tendon.joints : [];
            const referencesDeletedJoint = members.some((member: TendonJointMember) => {
              const jointRef =
                typeof member === "string"
                  ? member
                  : typeof member?.joint === "string"
                    ? member.joint
                    : null;
              return !!jointRef && deletedJointTargets.has(jointRef);
            });
            if (referencesDeletedJoint) {
              tendonUuidsToDelete.add(tendonUuid);
            }
          }
        );
        tendonUuidsToDelete.forEach((tendonUuid) => {
          const tendonKey = tendonUuid as TendonUUID;
          const tendon = draft.tendons?.[tendonKey];
          if (tendon) {
            deletedTendonTargets.add(tendonUuid);
            if (tendon.uuid) deletedTendonTargets.add(tendon.uuid);
            if (tendon.name) deletedTendonTargets.add(tendon.name);
          }
          delete draft.tendons[tendonKey];
        });

        // Cascade-delete actuators that target deleted joints/tendons.
        Object.entries((draft.actuators || {}) as Record<string, Actuator>).forEach(
          ([actuatorUuid, actuator]) => {
            if (!actuator) return;
            const jointTarget = typeof actuator.joint === "string" ? actuator.joint : null;
            const tendonTarget =
              typeof actuator.tendonUuid === "string"
                ? actuator.tendonUuid
                : typeof actuator.tendon === "string"
                  ? actuator.tendon
                  : null;
            if (
              (jointTarget && deletedJointTargets.has(jointTarget)) ||
              (tendonTarget && deletedTendonTargets.has(tendonTarget))
            ) {
              delete draft.actuators[actuatorUuid as ActuatorUUID];
            }
          }
        );

        // Remove equalities referencing deleted body/site/joint/tendon targets.
        const deletedTargets = new Set<string>([bodyUuid]);
        siteUuidsToDelete.forEach((uuid) => deletedTargets.add(uuid));
        deletedJointTargets.forEach((target) => deletedTargets.add(target));
        deletedTendonTargets.forEach((target) => deletedTargets.add(target));
        Object.entries((draft.equality || {}) as Record<string, Equality>).forEach(
          ([eqUuid, eq]) => {
            if (!eq) return;
            const obj1 = typeof eq.obj1 === "string" ? eq.obj1 : null;
            const obj2 = typeof eq.obj2 === "string" ? eq.obj2 : null;
            if ((obj1 && deletedTargets.has(obj1)) || (obj2 && deletedTargets.has(obj2))) {
              delete draft.equality[eqUuid as EqualityUUID];
            }
          }
        );

        cleanupReferencesForDeletedTargets({
          draft,
          deletedBodyUuids: new Set<string>([bodyUuid]),
          deletedSiteUuids: siteUuidsToDelete,
          cameras: { mode: "remove" },
        });

        embedSitesIntoBodies(updatedBodies, draft.sites);

        // Collect mesh UUIDs from the body being removed before the flat map is replaced.
        const candidateMeshUuidsForRemoveBody = collectGeomMeshUuids(draft.bodies as BodyRecord, [
          bodyUuid,
        ]);

        // Flatten back into Record
        const { bodies, worldBodies } = flattenBodyTree(updatedBodies as unknown as BodyTreeNode[]);
        draft.bodies = bodies;
        draft.worldBodies = worldBodies;
        draft.contactExcludes = (draft.contactExcludes || []).filter((exclude: ContactExclude) => {
          const body1 = typeof exclude?.body1 === "string" ? exclude.body1 : null;
          const body2 = typeof exclude?.body2 === "string" ? exclude.body2 : null;
          return !((body1 && body1 === bodyUuid) || (body2 && body2 === bodyUuid));
        });

        // Remove mesh assets that are no longer referenced by remaining geoms.
        pruneOrphanMeshes(draft, candidateMeshUuidsForRemoveBody);

        shouldDispatchStructureChanged = true;
      })
    );

    if (shouldDispatchStructureChanged) {
      cleanupReferencesForDeletedTargets({
        deletedBodyUuids: new Set<string>([bodyUuid]),
        external: { grasps: true },
      });
      try {
        window.dispatchEvent(new CustomEvent("structure-changed"));
      } catch (error) {
        logger.warn("[bodyActions] Failed to dispatch structure-changed after joint embed", error);
      }
    }
  }),

  // Remove the entire connected object (root body + full subtree) for the selected body UUID.
  removeBodyObject: withHistory((bodyUuid: BodyUUID) => {
    if (shouldBlockPhysicsMutation("removeBodyObject")) return;
    let shouldDispatchStructureChanged = false;
    const deletedBodyUuidsForExternalCleanup = new Set<string>();
    set((state) =>
      create(state, (draft) => {
        const bodiesMap: BodyRecord = draft.bodies || {};
        if (!bodiesMap[bodyUuid]) {
          logger.error(`[removeBodyObject] Body UUID not found: ${bodyUuid}`);
          return;
        }

        // Find the root body for the selected body by walking parent links.
        const findParentUuid = (targetUuid: string): string | null => {
          for (const [candidateUuid, candidateBody] of Object.entries(bodiesMap)) {
            if (!candidateBody || !Array.isArray(candidateBody.children)) continue;
            if (candidateBody.children.includes(targetUuid as BodyUUID)) {
              return candidateUuid;
            }
          }
          return null;
        };

        let rootUuid: string = bodyUuid;
        const visited = new Set<string>();
        while (rootUuid && !visited.has(rootUuid)) {
          visited.add(rootUuid);
          const parent = findParentUuid(rootUuid);
          if (!parent) break;
          rootUuid = parent;
        }

        // Collect full subtree for deletion.
        const bodyUuidsToDelete = new Set<string>();
        const stack: string[] = [rootUuid];
        while (stack.length > 0) {
          const current = stack.pop()!;
          if (!current || bodyUuidsToDelete.has(current)) continue;
          bodyUuidsToDelete.add(current);
          deletedBodyUuidsForExternalCleanup.add(current);
          const body = bodiesMap[current];
          const children = Array.isArray(body?.children) ? body.children : [];
          children.forEach((childUuid: string) => {
            if (childUuid && !bodyUuidsToDelete.has(childUuid)) stack.push(childUuid);
          });
        }

        // Collect and delete sites associated with subtree bodies.
        const siteUuidsToDelete = new Set<SiteUUID>();
        bodyUuidsToDelete.forEach((nodeUuid) => {
          const body = bodiesMap[nodeUuid];
          const siteEntries = Array.isArray(body?.sites) ? body.sites : [];
          siteEntries.forEach((entry: SiteUUID | Site) => {
            if (!entry) return;
            if (typeof entry === "string") {
              siteUuidsToDelete.add(entry as SiteUUID);
            } else {
              siteUuidsToDelete.add(entry.uuid as SiteUUID);
            }
          });
        });
        siteUuidsToDelete.forEach((siteUuid) => {
          delete draft.sites[siteUuid];
        });

        // Collect and delete joints referenced by subtree bodies.
        const jointKeysToDelete = new Set<string>();
        bodyUuidsToDelete.forEach((nodeUuid) => {
          const body = bodiesMap[nodeUuid];
          const jointRefs = Array.isArray(body?.joints) ? body.joints : [];
          jointRefs.forEach((ref: JointRef) => {
            if (typeof ref === "string") {
              if (draft.joints?.[ref as JointUUID]) {
                jointKeysToDelete.add(ref);
              }
              Object.entries(draft.joints as Record<string, Joint>).forEach(([key, joint]) => {
                if (joint && (joint.uuid === ref || joint.name === ref)) {
                  jointKeysToDelete.add(key);
                }
              });
              return;
            }
            if (ref && typeof ref === "object") {
              const identifier = ref.uuid ?? ref.name;
              if (!identifier) return;
              Object.entries(draft.joints as Record<string, Joint>).forEach(([key, joint]) => {
                if (joint && (joint.uuid === identifier || joint.name === identifier)) {
                  jointKeysToDelete.add(key);
                }
              });
            }
          });
        });
        const deletedJointTargets = new Set<string>();
        jointKeysToDelete.forEach((jointKey) => {
          const jointUuid = jointKey as JointUUID;
          const joint = draft.joints?.[jointUuid];
          if (joint) {
            deletedJointTargets.add(jointKey);
            if (joint.uuid) deletedJointTargets.add(joint.uuid);
            if (joint.name) deletedJointTargets.add(joint.name);
          }
          delete draft.joints[jointUuid];
        });

        // Cascade-delete tendons that reference deleted joints.
        const tendonUuidsToDelete = new Set<string>();
        const deletedTendonTargets = new Set<string>();
        Object.entries((draft.tendons || {}) as Record<string, Tendon>).forEach(
          ([tendonUuid, tendon]) => {
            if (!tendon) return;
            const members = Array.isArray(tendon.joints) ? tendon.joints : [];
            const referencesDeletedJoint = members.some((member: TendonJointMember) => {
              const jointRef =
                typeof member === "string"
                  ? member
                  : typeof member?.joint === "string"
                    ? member.joint
                    : null;
              return !!jointRef && deletedJointTargets.has(jointRef);
            });
            if (referencesDeletedJoint) {
              tendonUuidsToDelete.add(tendonUuid);
            }
          }
        );
        tendonUuidsToDelete.forEach((tendonUuid) => {
          const tendonKey = tendonUuid as TendonUUID;
          const tendon = draft.tendons?.[tendonKey];
          if (tendon) {
            deletedTendonTargets.add(tendonUuid);
            if (tendon.uuid) deletedTendonTargets.add(tendon.uuid);
            if (tendon.name) deletedTendonTargets.add(tendon.name);
          }
          delete draft.tendons[tendonKey];
        });

        // Cascade-delete actuators that target deleted joints/tendons.
        Object.entries((draft.actuators || {}) as Record<string, Actuator>).forEach(
          ([actuatorUuid, actuator]) => {
            if (!actuator) return;
            const jointTarget = typeof actuator.joint === "string" ? actuator.joint : null;
            const tendonTarget =
              typeof actuator.tendonUuid === "string"
                ? actuator.tendonUuid
                : typeof actuator.tendon === "string"
                  ? actuator.tendon
                  : null;
            if (
              (jointTarget && deletedJointTargets.has(jointTarget)) ||
              (tendonTarget && deletedTendonTargets.has(tendonTarget))
            ) {
              delete draft.actuators[actuatorUuid as ActuatorUUID];
            }
          }
        );

        // Remove equalities referencing deleted bodies/sites/joints/tendons.
        const deletedTargets = new Set<string>();
        bodyUuidsToDelete.forEach((uuid) => deletedTargets.add(uuid));
        siteUuidsToDelete.forEach((uuid) => deletedTargets.add(uuid));
        deletedJointTargets.forEach((target) => deletedTargets.add(target));
        deletedTendonTargets.forEach((target) => deletedTargets.add(target));
        Object.entries((draft.equality || {}) as Record<string, Equality>).forEach(
          ([eqUuid, eq]) => {
            if (!eq) return;
            const obj1 = typeof eq.obj1 === "string" ? eq.obj1 : null;
            const obj2 = typeof eq.obj2 === "string" ? eq.obj2 : null;
            if ((obj1 && deletedTargets.has(obj1)) || (obj2 && deletedTargets.has(obj2))) {
              delete draft.equality[eqUuid as EqualityUUID];
            }
          }
        );

        cleanupReferencesForDeletedTargets({
          draft,
          deletedBodyUuids: bodyUuidsToDelete,
          deletedSiteUuids: siteUuidsToDelete,
          cameras: { mode: "remove" },
        });

        // Collect mesh UUIDs referenced by the subtree before deleting its bodies.
        const candidateMeshUuids = collectGeomMeshUuids(bodiesMap, bodyUuidsToDelete);

        // Remove subtree bodies from parent links and body map.
        Object.values(bodiesMap).forEach((body) => {
          if (!body || !Array.isArray(body.children)) return;
          body.children = body.children.filter(
            (childUuid: string) => !bodyUuidsToDelete.has(childUuid)
          );
        });
        bodyUuidsToDelete.forEach((nodeUuid) => {
          delete draft.bodies[nodeUuid as BodyUUID];
          if (draft.bodyPositions) delete draft.bodyPositions[nodeUuid];
          if (draft.bodyRotations) delete draft.bodyRotations[nodeUuid];
        });

        // Remove mesh assets that are no longer referenced by remaining geoms.
        pruneOrphanMeshes(draft, candidateMeshUuids);

        // Remove deleted roots from worldBodies.
        draft.worldBodies = (draft.worldBodies || []).filter((wb: WorldBodyRef) => {
          const wbUuid = typeof wb === "string" ? wb : wb.uuid;
          return !bodyUuidsToDelete.has(wbUuid);
        });
        draft.contactExcludes = (draft.contactExcludes || []).filter((exclude: ContactExclude) => {
          const body1 = typeof exclude?.body1 === "string" ? exclude.body1 : null;
          const body2 = typeof exclude?.body2 === "string" ? exclude.body2 : null;
          return !(
            (body1 && bodyUuidsToDelete.has(body1)) ||
            (body2 && bodyUuidsToDelete.has(body2))
          );
        });

        shouldDispatchStructureChanged = true;
      })
    );

    if (shouldDispatchStructureChanged) {
      cleanupReferencesForDeletedTargets({
        deletedBodyUuids: deletedBodyUuidsForExternalCleanup,
        external: { grasps: true },
      });
      try {
        window.dispatchEvent(new CustomEvent("structure-changed"));
      } catch (error) {
        logger.warn(
          "[bodyActions] Failed to dispatch structure-changed after object removal",
          error
        );
      }
    }
  }),
});
